import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, MessageSquare } from 'lucide-react'
import { useToast } from '../components/common/Toast'
import Footer from '../components/layout/Footer'
import api from '../services/api'
import { CONTACT_INQUIRY_TYPES } from '../constants/supportTicketSubjects'

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: 'Sales',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const toast = useToast()

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await api.support.submitContactInquiry({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        inquiryType: formData.inquiryType,
        message: formData.message,
      })
      setSubmitted(true)
      setFormData({
        name: '',
        email: '',
        company: '',
        inquiryType: 'Sales',
        message: '',
      })
      toast.success("Thanks! We'll be in touch soon.")
    } catch (error) {
      toast.error('Something went wrong. Please try again.')
      console.error('Contact form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="bg-gradient-to-br from-primary-50 to-accent-50 border-b border-gray-100">
        <div className="max-w-xl mx-auto px-6 py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-primary flex items-center justify-center shadow-glow">
              <MessageSquare className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Contact Us</h1>
              <p className="text-gray-500">We&apos;d love to hear from you</p>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-xl mx-auto px-6 py-12 w-full">
        {submitted ? (
          <p className="text-gray-600 leading-relaxed">
            Thank you for reaching out. Our team will get back to you shortly.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                autoComplete="name"
                className="input-field w-full"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                autoComplete="email"
                className="input-field w-full"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                Company
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                autoComplete="organization"
                className="input-field w-full"
                placeholder="Your company"
              />
            </div>

            <div>
              <label htmlFor="inquiryType" className="block text-sm font-medium text-gray-700 mb-2">
                Inquiry Type
              </label>
              <select
                id="inquiryType"
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleInputChange}
                required
                className="input-field w-full"
              >
                {CONTACT_INQUIRY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Message <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={5}
                className="input-field w-full resize-vertical"
                placeholder="Tell us more about your inquiry..."
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </form>
        )}
        <p className="text-center text-gray-600 mt-8 text-sm">
          Need product help? Visit our{' '}
          <Link to="/support" className="text-primary-600 font-medium hover:underline">
            Support Center
          </Link>{' '}
          or browse{' '}
          <Link to="/faqs" className="text-primary-600 font-medium hover:underline">
            FAQs
          </Link>
          .
        </p>
      </main>

      <Footer />
    </div>
  )
}

export default ContactUs
